import { useState, useEffect } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { 
  ShieldCheck, 
  ScanEye, 
  Bell, 
  FileText, 
  Clock, 
  AudioLines,
  Check,
  ChevronRight,
  ChevronUp,
  Phone,
  Mail,
  MapPin,
  Shield,
  AlertTriangle,
  TrendingUp,
  Users,
  Award,
  ArrowRight,
  Loader2,
  Star,
  Calendar,
  Video,
  Menu,
  X,
  ExternalLink,
  Eye,
  Search,
  AlertCircle,
  Send
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { API } from "@/App";
import { SEOHead, serviceSchema, breadcrumbSchema } from "@/components/SEOHead";

const NBR_LOGO = "https://nationalbusinessregister.co.uk/wp-content/uploads/2024/11/logo.png";

const COLORS = {
  primary: "#71edcc",
  primaryDark: "#149b7f",
  primaryLight: "#a9f4e4",
  navy: "#1e293b",
  gold: "#f1b440",
  white: "#ffffff",
  lightBg: "#f8fafc",
  grayText: "#64748b"
};

const PRICING_TIERS = [
  { min: 1, max: 1, monthlyPerTM: 20, yearlyPerTM: 240, discount: 0, label: "1 Trade Mark" },
  { min: 2, max: 5, monthlyPerTM: 18, yearlyPerTM: 216, discount: 10, label: "2-5 Trade Marks" },
  { min: 6, max: 10, monthlyPerTM: 16, yearlyPerTM: 192, discount: 20, label: "6-10 Trade Marks" },
  { min: 11, max: Infinity, monthlyPerTM: 14, yearlyPerTM: 168, discount: 30, label: "11+ Trade Marks" }
];

const ANNUAL_PREPAY_DISCOUNT = 10;
const BOOKINGS_URL = "https://bookings.cloud.microsoft/book/NationalBusinessRegisterGroupLimitedNBR@nbrg.co.uk/s/mbmhEOVCOEG72cEdq6hN4Q2?ismsaljsauthenabled=true";

const calculatePricing = (numTrademarks, isAnnual = false) => {
  const tier = PRICING_TIERS.find(t => numTrademarks >= t.min && numTrademarks <= t.max);
  if (!tier) return null;
  const monthlyPerTM = tier.monthlyPerTM;
  const yearlyPerTM = tier.yearlyPerTM;
  const monthlyTotal = monthlyPerTM * numTrademarks;
  let yearlyTotal = yearlyPerTM * numTrademarks;
  if (isAnnual) yearlyTotal = yearlyTotal * (1 - ANNUAL_PREPAY_DISCOUNT / 100);
  const fullPriceMonthly = 20 * numTrademarks;
  const fullPriceYearly = 240 * numTrademarks;
  return {
    tier, numTrademarks, monthlyPerTM, yearlyPerTM, monthlyTotal,
    yearlyTotal: Math.round(yearlyTotal * 100) / 100,
    monthlySavings: fullPriceMonthly - monthlyTotal,
    yearlySavings: Math.round((fullPriceYearly - yearlyTotal) * 100) / 100,
    volumeDiscount: tier.discount,
    annualDiscount: isAnnual ? ANNUAL_PREPAY_DISCOUNT : 0,
    effectiveYearlyPerTM: isAnnual ? Math.round((yearlyTotal / numTrademarks) * 100) / 100 : yearlyPerTM
  };
};

// Newsletter Signup Component
const NewsletterSignup = () => {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [subscribed, setSubscribed] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      const res = await axios.post(`${API}/newsletter/subscribe`, { email });
      setSubscribed(true);
      toast.success(res.data.message);
      setEmail("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (subscribed) {
    return (
      <div className="bg-teal-50 border border-teal-200 rounded-xl p-6 text-center">
        <Check className="w-8 h-8 text-teal-600 mx-auto mb-2" />
        <p className="text-teal-700 font-medium">Thank you for subscribing!</p>
        <p className="text-teal-600 text-sm mt-1">We will keep you updated on trade mark monitoring news and insights.</p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-3 max-w-lg mx-auto">
      <input
        type="email"
        placeholder="Enter your email address"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        required
        className="flex-1 px-4 py-3 rounded-lg border border-slate-300 focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 outline-none text-sm"
        data-testid="newsletter-email-input"
      />
      <Button
        type="submit"
        disabled={loading}
        className="bg-teal-600 hover:bg-teal-700 text-white px-6 py-3 whitespace-nowrap"
        data-testid="newsletter-submit-btn"
      >
        {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
        Subscribe
      </Button>
    </form>
  );
};

// Pricing Calculator Component
const PricingCalculator = ({ handleSubscribe, isProcessing }) => {
  const [numTrademarks, setNumTrademarks] = useState(1);
  const [isAnnual, setIsAnnual] = useState(false);
  const pricing = calculatePricing(numTrademarks, isAnnual);
  
  return (
    <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
      className="bg-gradient-to-br from-slate-900 to-slate-800 rounded-2xl p-6 sm:p-8 lg:p-10 mb-12">
      <div className="grid lg:grid-cols-2 gap-8 items-center">
        <div>
          <h3 className="text-xl sm:text-2xl font-bold text-white mb-2">Calculate Your Investment</h3>
          <p className="text-slate-400 mb-6">Enter the number of UK trade marks you wish to monitor</p>
          <div className="mb-6">
            <label className="block text-sm font-medium text-slate-300 mb-2">Number of Trade Marks</label>
            <div className="flex items-center gap-4">
              <button onClick={() => setNumTrademarks(Math.max(1, numTrademarks - 1))} className="w-12 h-12 rounded-lg bg-slate-700 text-white font-bold hover:bg-slate-600 transition-colors">-</button>
              <input type="number" min="1" max="100" value={numTrademarks} onChange={(e) => setNumTrademarks(Math.max(1, Math.min(100, parseInt(e.target.value) || 1)))} className="w-24 h-12 text-center text-2xl font-bold bg-white text-slate-900 rounded-lg border-0" />
              <button onClick={() => setNumTrademarks(Math.min(100, numTrademarks + 1))} className="w-12 h-12 rounded-lg bg-slate-700 text-white font-bold hover:bg-slate-600 transition-colors">+</button>
            </div>
          </div>
          <div className="mb-6">
            <input type="range" min="1" max="25" value={Math.min(25, numTrademarks)} onChange={(e) => setNumTrademarks(parseInt(e.target.value))} className="w-full h-2 bg-slate-700 rounded-lg appearance-none cursor-pointer accent-teal-500" />
            <div className="flex justify-between text-xs text-slate-500 mt-1"><span>1</span><span>5</span><span>10</span><span>15</span><span>20</span><span>25+</span></div>
          </div>
          <div className="flex items-center gap-4 p-4 bg-slate-800 rounded-lg">
            <span className={`text-sm ${!isAnnual ? 'text-white font-medium' : 'text-slate-400'}`}>Monthly</span>
            <button onClick={() => setIsAnnual(!isAnnual)} className={`relative w-14 h-7 rounded-full transition-colors ${isAnnual ? 'bg-teal-600' : 'bg-slate-600'}`}>
              <div className={`absolute top-1 w-5 h-5 bg-white rounded-full transition-transform ${isAnnual ? 'translate-x-8' : 'translate-x-1'}`} />
            </button>
            <span className={`text-sm ${isAnnual ? 'text-white font-medium' : 'text-slate-400'}`}>Annual <span className="ml-1 text-xs text-teal-400">(+10% off)</span></span>
          </div>
        </div>
        <div className="bg-white rounded-xl p-6 sm:p-8">
          <div className="flex items-center justify-between mb-4">
            <span className="text-sm font-medium text-slate-500">Your tier</span>
            <span className={`text-sm font-bold px-3 py-1 rounded-full ${pricing.volumeDiscount > 0 ? 'bg-teal-100 text-teal-700' : 'bg-slate-100 text-slate-700'}`}>
              {pricing.tier.label}{pricing.volumeDiscount > 0 && ` (${pricing.volumeDiscount}% off)`}
            </span>
          </div>
          <div className="border-b border-slate-200 pb-4 mb-4">
            <p className="text-sm text-slate-500 mb-1">Per trade mark</p>
            <div className="flex items-baseline gap-2">
              <span className="text-3xl font-black text-slate-900">£{isAnnual ? pricing.effectiveYearlyPerTM.toFixed(2) : pricing.monthlyPerTM}</span>
              <span className="text-slate-500">/{isAnnual ? 'year' : 'month'}</span>
              {pricing.volumeDiscount > 0 && <span className="text-sm text-slate-400 line-through">£{isAnnual ? '240' : '20'}</span>}
            </div>
          </div>
          <div className="mb-6">
            <p className="text-sm text-slate-500 mb-1">Total for {numTrademarks} trade mark{numTrademarks > 1 ? 's' : ''}</p>
            <div className="flex items-baseline gap-2">
              <span className="text-4xl font-black text-teal-600">£{isAnnual ? pricing.yearlyTotal.toFixed(2) : pricing.monthlyTotal}</span>
              <span className="text-slate-500">/{isAnnual ? 'year' : 'month'}</span>
            </div>
            {(pricing.monthlySavings > 0 || pricing.yearlySavings > 0) && (
              <div className="mt-2 inline-block bg-amber-50 text-amber-700 text-sm font-medium px-3 py-1 rounded-full">
                You save £{isAnnual ? pricing.yearlySavings.toFixed(2) : pricing.monthlySavings}/{isAnnual ? 'year' : 'month'}
              </div>
            )}
          </div>
          <div className="bg-slate-50 rounded-lg p-4 mb-6 text-sm">
            <div className="flex justify-between mb-1"><span className="text-slate-600">{numTrademarks} x £{pricing.monthlyPerTM}/month</span><span className="text-slate-900">£{pricing.monthlyTotal}/month</span></div>
            {isAnnual && (<><div className="flex justify-between mb-1"><span className="text-slate-600">Annual (x12)</span><span className="text-slate-900">£{pricing.monthlyTotal * 12}</span></div><div className="flex justify-between text-teal-600"><span>Annual prepay discount (-10%)</span><span>-£{((pricing.monthlyTotal * 12) - pricing.yearlyTotal).toFixed(2)}</span></div></>)}
          </div>
          <Button size="lg" onClick={handleSubscribe} disabled={isProcessing} className="w-full bg-teal-600 hover:bg-teal-700 text-white py-6 shadow-lg">
            {isProcessing ? <Loader2 className="w-5 h-5 animate-spin mr-2" /> : <ShieldCheck className="w-5 h-5 mr-2" />}
            Start Monitoring my Trade Mark
          </Button>
          <p className="text-center text-xs text-slate-500 mt-3">No long-term commitment. Cancel anytime</p>
        </div>
      </div>
    </motion.div>
  );
};

export const LandingPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [isProcessing, setIsProcessing] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showBackToTop, setShowBackToTop] = useState(false);

  useEffect(() => {
    const handleScroll = () => setShowBackToTop(window.scrollY > 600);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    if (sessionId) pollPaymentStatus(sessionId);
  }, [searchParams]);

  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    const maxAttempts = 10;
    if (attempts >= maxAttempts) { setPaymentStatus('timeout'); toast.error('Payment status check timed out. Please contact support.'); return; }
    try {
      const response = await axios.get(`${API}/payments/status/${sessionId}`);
      const data = response.data;
      if (data.payment_status === 'paid') { setPaymentStatus('success'); toast.success('Payment successful! We will be in touch shortly.'); return; }
      else if (data.status === 'expired') { setPaymentStatus('expired'); toast.error('Payment session expired. Please try again.'); return; }
      setPaymentStatus('processing');
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), 2000);
    } catch (error) {
      if (attempts < maxAttempts - 1) setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), 2000);
      else { setPaymentStatus('error'); toast.error('Error checking payment status.'); }
    }
  };

  const handleSubscribe = async () => { navigate('/register-trademarks'); window.scrollTo(0, 0); };

  if (paymentStatus === 'processing') return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center"><div className="text-center"><Loader2 className="w-12 h-12 animate-spin text-teal-600 mx-auto mb-4" /><h2 className="text-xl font-bold text-slate-900">Processing your payment...</h2><p className="text-slate-500 mt-2">Please wait while we confirm your subscription.</p></div></div>
  );

  if (paymentStatus === 'success') return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center"><div className="text-center max-w-md"><div className="w-20 h-20 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-6"><Check className="w-10 h-10 text-teal-600" /></div><h2 className="text-2xl font-bold text-slate-900 mb-2">Payment Successful!</h2><p className="text-slate-500 mb-6">Thank you for subscribing to NBR Trade Mark Monitoring. We are currently setting up your account and will be in touch shortly with your login details.</p><Button onClick={() => navigate('/')} className="bg-teal-600 hover:bg-teal-700">Return to Home</Button></div></div>
  );

  return (
    <div className="min-h-screen bg-white">
      <SEOHead
        title="UK Trade Mark Monitoring Service"
        description="Defend your registered UK trade marks against newly filed applications. NBR Trade Mark Monitoring scans UKIPO filings for similarity against your marks, identifying potentially conflicting applications early. From £14/month. 40+ years IP expertise."
        path="/landing"
        schema={{
          "@context": "https://schema.org",
          "@graph": [
            serviceSchema,
            breadcrumbSchema([
              { name: "Home", url: "/landing" }
            ])
          ]
        }}
      />
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-md border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <img src={NBR_LOGO} alt="National Business Register" className="h-8 sm:h-10 w-auto" />
              <div className="hidden sm:block">
                <span className="font-heading font-black text-lg text-slate-900">NBR</span>
                <span className="text-xs text-teal-600 tracking-widest uppercase ml-1">Trade Mark Monitoring</span>
              </div>
            </div>
            <div className="hidden md:flex items-center gap-6">
              <a href="#methodology" className="text-sm text-slate-600 hover:text-teal-600 transition-colors">Features</a>
              <a href="#pricing" className="text-sm text-slate-600 hover:text-teal-600 transition-colors">Pricing</a>
              <a href="#how-it-works" className="text-sm text-slate-600 hover:text-teal-600 transition-colors">How It Works</a>
              <Link to="/about" className="text-sm text-slate-600 hover:text-teal-600 transition-colors">About You</Link>
              <Link to="/faq" className="text-sm text-slate-600 hover:text-teal-600 transition-colors">FAQs</Link>
              <a href={BOOKINGS_URL} target="_blank" rel="noopener noreferrer" className="text-sm text-slate-600 hover:text-teal-600 transition-colors">Book a Demo</a>
              <Link to="/blog" className="text-sm text-slate-600 hover:text-teal-600 transition-colors">Blog</Link>
              <a href="tel:08000699090" className="text-sm text-slate-600 hover:text-teal-600 flex items-center gap-1 transition-colors"><Phone className="w-4 h-4" />0800 069 9090</a>
            </div>
            <div className="flex items-center gap-2 sm:gap-3">
              <Button onClick={handleSubscribe} disabled={isProcessing} className="bg-teal-600 hover:bg-teal-700 text-white text-xs sm:text-sm px-3 sm:px-4" data-testid="subscribe-btn-nav">
                {isProcessing ? <Loader2 className="w-4 h-4 animate-spin" /> : "Start Monitoring"}
              </Button>
              <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="md:hidden p-2 text-slate-600 hover:text-teal-600 transition-colors" aria-label="Toggle menu">
                {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          </div>
        </div>
        {mobileMenuOpen && (
          <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className="md:hidden bg-white border-t border-slate-100 shadow-lg">
            <div className="px-4 py-4 space-y-3">
              <a href="#methodology" onClick={() => setMobileMenuOpen(false)} className="block py-2 text-slate-600 hover:text-teal-600 font-medium">Features</a>
              <a href="#pricing" onClick={() => setMobileMenuOpen(false)} className="block py-2 text-slate-600 hover:text-teal-600 font-medium">Pricing</a>
              <a href="#how-it-works" onClick={() => setMobileMenuOpen(false)} className="block py-2 text-slate-600 hover:text-teal-600 font-medium">How It Works</a>
              <Link to="/about" onClick={() => setMobileMenuOpen(false)} className="block py-2 text-slate-600 hover:text-teal-600 font-medium">About You</Link>
              <Link to="/faq" onClick={() => setMobileMenuOpen(false)} className="block py-2 text-slate-600 hover:text-teal-600 font-medium">FAQs</Link>
              <a href={BOOKINGS_URL} target="_blank" rel="noopener noreferrer" onClick={() => setMobileMenuOpen(false)} className="block py-2 text-slate-600 hover:text-teal-600 font-medium">Book a Demo</a>
              <Link to="/blog" onClick={() => setMobileMenuOpen(false)} className="block py-2 text-slate-600 hover:text-teal-600 font-medium">Blog</Link>
              <a href="tel:08000699090" className="flex items-center gap-2 py-2 text-teal-600 font-medium"><Phone className="w-4 h-4" />0800 069 9090</a>
            </div>
          </motion.div>
        )}
      </nav>

      {/* Hero Section - Point 12, 13, 2, 23, 24 */}
      <section className="pt-24 sm:pt-32 pb-12 sm:pb-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-slate-50 to-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-start">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="pt-2">
              <div className="inline-flex items-center gap-2 bg-teal-50 text-teal-700 px-3 sm:px-4 py-1.5 sm:py-2 rounded-full text-xs sm:text-sm font-medium mb-4 sm:mb-6 border border-teal-200">
                <Shield className="w-3 h-3 sm:w-4 sm:h-4" />
                UK-Focused Service | Designed for Business Owners
              </div>
              <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-heading font-black text-slate-900 leading-tight mb-4 sm:mb-6">
                Defend Your Registered UK Trade Marks Against New Filings
              </h1>
              {/* Positioning statement - Point 13 */}
              <p className="text-base sm:text-lg text-slate-600 mb-6 sm:mb-8 leading-relaxed">
                NBR Trade Mark Monitoring scans newly filed and published UK trade mark applications for similarity 
                against your already registered marks, helping you identify potentially conflicting filings early 
                while opposition and observation options remain available.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
                {/* Point 2: Speak to a Specialist = phone call */}
                <Button 
                  size="lg"
                  onClick={() => window.location.href = 'tel:08000699090'}
                  className="bg-teal-600 hover:bg-teal-700 text-white px-6 sm:px-8 py-5 sm:py-6 text-sm sm:text-base shadow-lg shadow-teal-600/20"
                  data-testid="subscribe-btn-hero"
                >
                  <Phone className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
                  Call a Specialist: 0800 069 9090
                </Button>
                {/* Point 2: Book Free Consultation = Teams booking */}
                <Button 
                  variant="outline" 
                  size="lg"
                  onClick={() => window.open(BOOKINGS_URL, '_blank')}
                  className="px-6 sm:px-8 py-5 sm:py-6 text-sm sm:text-base border-slate-300 hover:bg-[#f1b440] hover:text-white hover:border-[#f1b440] transition-all"
                  data-testid="consultation-btn"
                >
                  <Video className="w-4 h-4 mr-2" />
                  Book a Free Teams Consultation
                </Button>
                {/* Start Monitoring button */}
                <Button 
                  size="lg"
                  onClick={handleSubscribe}
                  className="bg-slate-900 hover:bg-slate-800 text-white px-6 sm:px-8 py-5 sm:py-6 text-sm sm:text-base"
                  data-testid="start-monitoring-hero-btn"
                >
                  <ShieldCheck className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
                  Start Monitoring
                </Button>
              </div>
              {/* Trust signals - Point 24 */}
              <div className="mt-6 sm:mt-8 flex flex-wrap items-center gap-3 sm:gap-6 text-xs sm:text-sm text-slate-500">
                <div className="flex items-center gap-1.5 sm:gap-2"><Check className="w-3 h-3 sm:w-4 sm:h-4 text-teal-500" />UK-focused service</div>
                <div className="flex items-center gap-1.5 sm:gap-2"><Check className="w-3 h-3 sm:w-4 sm:h-4 text-teal-500" />Designed for business owners</div>
                <div className="flex items-center gap-1.5 sm:gap-2"><Check className="w-3 h-3 sm:w-4 sm:h-4 text-teal-500" />40+ years IP expertise</div>
              </div>
            </motion.div>
            
            {/* Pricing Box */}
            <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.6, delay: 0.2 }} className="relative">
              <div className="bg-white border-2 border-teal-600 rounded-2xl p-6 lg:p-8 relative z-10 shadow-2xl">
                <div className="absolute top-0 right-0 bg-teal-600 text-white text-xs font-bold px-3 py-1 rounded-bl-lg rounded-tr-xl">VOLUME DISCOUNTS</div>
                <div className="pt-4">
                  <p className="text-sm text-slate-500 font-medium mb-1">Professional Monitoring</p>
                  <h3 className="text-xl font-bold text-slate-900 mb-3">From</h3>
                  <div className="flex items-baseline gap-2 mb-2">
                    <span className="text-4xl lg:text-5xl font-black text-teal-600">£14</span>
                    <span className="text-slate-500">/trade mark/month</span>
                  </div>
                  <p className="text-sm text-slate-600 mb-4">
                    <span className="line-through text-slate-400">£20</span> - Save up to 30% with portfolio pricing
                  </p>
                  <div className="space-y-2 mb-6 border-t border-slate-100 pt-4">
                    <div className="flex justify-between text-sm"><span className="text-slate-600">1 Trade Mark</span><span className="font-semibold text-slate-900">£20/month</span></div>
                    <div className="flex justify-between text-sm"><span className="text-slate-600">2-5 Trade Marks</span><span className="font-semibold text-teal-600">£18/month <span className="text-xs text-slate-400">each</span></span></div>
                    <div className="flex justify-between text-sm"><span className="text-slate-600">6-10 Trade Marks</span><span className="font-semibold text-teal-600">£16/month <span className="text-xs text-slate-400">each</span></span></div>
                    <div className="flex justify-between text-sm"><span className="text-slate-600">11+ Trade Marks</span><span className="font-semibold text-teal-600">£14/month <span className="text-xs text-slate-400">each</span></span></div>
                  </div>
                  <Button size="lg" onClick={() => document.getElementById('pricing').scrollIntoView({ behavior: 'smooth' })} className="w-full bg-teal-600 hover:bg-teal-700 text-white py-5 shadow-lg shadow-teal-600/20" data-testid="view-pricing-btn">
                    <ShieldCheck className="w-5 h-5 mr-2" />Calculate Your Price
                  </Button>
                  <p className="text-center text-xs text-slate-500 mt-3">+10% off annual prepayment</p>
                </div>
              </div>
              <div className="absolute -bottom-4 -right-4 w-full h-full bg-teal-100 rounded-2xl -z-0" />
            </motion.div>
          </div>
        </div>
      </section>

      {/* What is Trade Mark Monitoring - Points 3, 14 */}
      <section className="bg-white py-12 sm:py-16 px-4 sm:px-6 lg:px-8 border-y border-slate-100">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-xl sm:text-2xl md:text-3xl font-heading font-black text-slate-900 mb-4 sm:mb-6">
            What is Trade Mark Monitoring?
          </h2>
          <div className="bg-slate-50 rounded-xl sm:rounded-2xl p-5 sm:p-8 border border-slate-200">
            <p className="text-base text-slate-700 leading-relaxed mb-4">
              <strong>Trade mark monitoring</strong> (also known as trade mark watching or brand protection monitoring) 
              is a post-registration intellectual property service that scans newly filed and published trade mark 
              applications for potential conflicts with your existing registered marks.
            </p>
            <p className="text-base text-slate-700 leading-relaxed mb-4">
              The service identifies identical marks, confusingly similar names, and applications 
              with phonetic, visual, or conceptual similarities to your trade marks, including 
              conflicts in relevant goods and services classifications.
            </p>
            <p className="text-base text-slate-700 leading-relaxed">
              Unlike one-off trade mark searches (which only check existing registrations at a single point in time), 
              <strong> ongoing monitoring</strong> identifies new potentially conflicting filings as they arise, giving you 
              the opportunity to take action within the 2-month opposition window before a conflicting mark becomes registered.
            </p>
          </div>
        </div>
      </section>

      {/* Trust Stats Bar - Point 24 */}
      <section className="bg-slate-900 py-8 sm:py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-8">
            {[
              { value: "40+", label: "Years of IP Experience" },
              { value: "10,000+", label: "Trade Marks Protected" },
              { value: "UK", label: "Focused Service" },
              { value: "UKIPO", label: "Data Source" }
            ].map((stat, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.1 }} className="text-center">
                <div className="text-2xl sm:text-3xl md:text-4xl font-black text-teal-400 mb-1">{stat.value}</div>
                <div className="text-slate-400 text-xs sm:text-sm">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Monitoring Matters - Point 22 */}
      <section className="py-12 sm:py-16 px-4 sm:px-6 lg:px-8 bg-teal-50 border-b border-teal-100">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-2xl sm:text-3xl font-heading font-black text-slate-900 mb-6">
            Why Monitoring Matters
          </h2>
          <div className="grid sm:grid-cols-3 gap-6">
            {[
              { icon: Calendar, title: "New trade marks are filed every day", desc: "The UK IPO register receives thousands of new applications each year across all industries." },
              { icon: Eye, title: "Similar names can slip through unnoticed", desc: "Without regular monitoring, potentially conflicting marks can progress to registration without challenge." },
              { icon: Shield, title: "Early action is far cheaper than legal disputes", desc: "Identifying applications of interest early means you can act while opposition and observation options remain available." }
            ].map((item, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.1 }} className="bg-white rounded-xl p-6 border border-teal-200">
                <item.icon className="w-10 h-10 text-teal-600 mx-auto mb-3" />
                <h3 className="font-bold text-slate-900 mb-2">{item.title}</h3>
                <p className="text-slate-600 text-sm">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* What We Detect / Methodology - Points 6, 16, 19 */}
      <section id="methodology" className="py-12 sm:py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-8 sm:mb-16">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-black text-slate-900 mb-3 sm:mb-4">
              How Similarity Is Identified
            </h2>
            <p className="text-base sm:text-lg text-slate-600 max-w-2xl mx-auto">
              Our pattern-based analysis identifies applications of interest across multiple dimensions of similarity. 
              This is automated pattern analysis and does not constitute a legal opinion.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-8">
            {[
              { icon: ScanEye, title: "Identical Marks", description: "Detects exact matches and near-identical applications that may conflict with your registered marks." },
              { icon: Bell, title: "Confusingly Similar Marks", description: "Identifies marks that consumers might mistake for yours, flagged as applications requiring review." },
              { icon: AudioLines, title: "Phonetic Similarities", description: "Catches marks that sound similar when spoken aloud, a common basis for trade mark disputes." },
              { icon: TrendingUp, title: "Visual Similarities", description: "Compares logos and stylised marks for visual conflicts using pattern-based image analysis." },
              { icon: FileText, title: "Dominant Element Analysis", description: "Examines the dominant and distinctive elements of marks to identify conceptual similarities." },
              { icon: Shield, title: "Goods & Services Conflicts", description: "Reviews relevant Nice classifications to identify potentially conflicting filings in your industry." }
            ].map((feature, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.1 }} className="bg-slate-50 border border-slate-200 rounded-xl p-4 sm:p-6 hover:shadow-lg hover:border-teal-200 transition-all">
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-teal-100 rounded-lg flex items-center justify-center mb-3 sm:mb-4">
                  <feature.icon className="w-5 h-5 sm:w-6 sm:h-6 text-teal-600" />
                </div>
                <h3 className="font-bold text-base sm:text-lg text-slate-900 mb-2">{feature.title}</h3>
                <p className="text-slate-600 text-sm leading-relaxed">{feature.description}</p>
              </motion.div>
            ))}
          </div>
          {/* Disclaimer - Point 17 */}
          <div className="mt-8 bg-slate-50 border border-slate-200 rounded-xl p-5 text-center">
            <p className="text-sm text-slate-500">
              <AlertCircle className="w-4 h-4 inline mr-1.5 -mt-0.5" />
              Results are intended to support opposition and enforcement decision-making and do not constitute clearance advice or legal conclusions.
            </p>
          </div>
        </div>
      </section>

      {/* Who Benefits */}
      <section className="py-12 sm:py-20 px-4 sm:px-6 lg:px-8 bg-slate-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-8 sm:mb-16">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-black text-slate-900 mb-3 sm:mb-4">
              Who Benefits From Trade Mark Monitoring?
            </h2>
            <p className="text-base sm:text-lg text-slate-600 max-w-2xl mx-auto">
              Designed for UK trade mark owners and businesses with registered marks who want proactive protection.
            </p>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6">
            {[
              { icon: Users, title: "Brand Owners & Founders", description: "Protect the brand you have worked hard to build from potentially conflicting filings." },
              { icon: Award, title: "In-House Legal & IP Teams", description: "Streamline trade mark watching with professional reports and specialist support." },
              { icon: TrendingUp, title: "Growing Businesses", description: "Scale your brand protection as your trade mark portfolio expands." },
              { icon: Shield, title: "Marketing & Brand Teams", description: "Maintain brand integrity and protect marketing investments from dilution." }
            ].map((audience, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.1 }} className="bg-white rounded-xl p-4 sm:p-6 border border-slate-200 text-center">
                <div className="w-10 h-10 sm:w-14 sm:h-14 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-3 sm:mb-4">
                  <audience.icon className="w-5 h-5 sm:w-7 sm:h-7 text-teal-600" />
                </div>
                <h3 className="font-bold text-sm sm:text-base text-slate-900 mb-1 sm:mb-2">{audience.title}</h3>
                <p className="text-slate-600 text-xs sm:text-sm hidden sm:block">{audience.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works - Points 8, 9, 10, 15, 18 */}
      <section id="how-it-works" className="py-12 sm:py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-8 sm:mb-16">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-black text-slate-900 mb-3 sm:mb-4">
              How It Works
            </h2>
            <p className="text-base sm:text-lg text-slate-600 max-w-2xl mx-auto">
              We use data from the UK Intellectual Property Office (UKIPO) to provide comprehensive 
              post-registration trade mark monitoring.
            </p>
          </div>

          {/* Point 15: Specific 3 steps */}
          <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-4 sm:gap-8 mb-8 sm:mb-16">
            {[
              {
                step: "01",
                title: "Your Registered Mark",
                description: "Your registered UK trade mark is used as the reference point. Register your existing marks "
              },
              {
                step: "02",
                title: "New Filings Are Scanned",
                description: "Newly filed and published UKIPO trade mark applications are scanned against your portfolio using pattern-based analysis of visual, phonetic, and dominant element similarities."
              },
              {
                step: "03",
                title: "Applications of Interest Identified",
                description: "Potential similarities are flagged as applications requiring review by our expert team, who prepare a professional report for your consideration."
              }
            ].map((item, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.15 }} className="relative bg-slate-50 rounded-xl p-4 sm:p-6 border border-slate-200">
                <div className="text-4xl sm:text-6xl font-black text-teal-100 absolute top-2 sm:top-4 right-2 sm:right-4">{item.step}</div>
                <div className="relative z-10">
                  <div className="w-8 h-8 sm:w-10 sm:h-10 bg-teal-600 text-white rounded-full flex items-center justify-center font-bold text-sm sm:text-base mb-3 sm:mb-4">{item.step}</div>
                  <h3 className="font-bold text-base sm:text-xl text-slate-900 mb-2">{item.title}</h3>
                  <p className="text-slate-600 leading-relaxed">
                    {i === 0 ? (
                      <>Your registered UK trade mark is used as the reference point. Register your existing marks <Link to="/register-trademarks" className="text-teal-600 hover:underline font-medium">here</Link>. We monitor word marks, logos, and combined marks.</>
                    ) : item.description}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>

          {/* What Happens After - Point 7 */}
          <div className="bg-teal-50 rounded-2xl p-8 border border-teal-200">
            <h3 className="text-xl font-bold text-slate-900 mb-6 text-center">What Happens When Applications of Interest Are Found?</h3>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 bg-teal-600 text-white rounded-full flex items-center justify-center flex-shrink-0 text-sm font-bold">1</div>
                <p className="text-slate-700">You receive a professional report from our expert team highlighting potentially conflicting filings</p>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 bg-teal-600 text-white rounded-full flex items-center justify-center flex-shrink-0 text-sm font-bold">2</div>
                <p className="text-slate-700">Consult with a trade mark specialist (included in your subscription)</p>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 bg-teal-600 text-white rounded-full flex items-center justify-center flex-shrink-0 text-sm font-bold">3</div>
                <p className="text-slate-700">Decide whether to file an opposition or take other action while options remain available</p>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 bg-teal-600 text-white rounded-full flex items-center justify-center flex-shrink-0 text-sm font-bold">4</div>
                <p className="text-slate-700">NBR can handle opposition proceedings on your behalf if required</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Data Source & Scope - Points 20, 21 */}
      <section className="py-12 sm:py-16 px-4 sm:px-6 lg:px-8 bg-slate-50">
        <div className="max-w-5xl mx-auto">
          <div className="grid md:grid-cols-2 gap-8">
            {/* Data Source Coverage - Point 21 */}
            <div className="bg-white rounded-xl p-6 sm:p-8 border border-slate-200">
              <h3 className="text-lg sm:text-xl font-bold text-slate-900 mb-4 flex items-center gap-2">
                <Search className="w-5 h-5 text-teal-600" /> Data Source Coverage
              </h3>
              <p className="text-slate-600 mb-4">
                This tool interrogates the UK trade mark register maintained by the UK Intellectual Property Office 
                and focuses on UK filings only.
              </p>
              <ul className="space-y-2 text-sm text-slate-600">
                <li className="flex items-center gap-2"><Check className="w-4 h-4 text-teal-600" /> Weekly scanning of UKIPO journal publications</li>
                <li className="flex items-center gap-2"><Check className="w-4 h-4 text-teal-600" /> Pattern-based similarity analysis</li>
                <li className="flex items-center gap-2"><Check className="w-4 h-4 text-teal-600" /> Opposition deadline tracking</li>
              </ul>
              <p className="text-xs text-slate-400 mt-4">EUIPO and WIPO coverage coming soon.</p>
            </div>

            {/* Scope Limitations - Point 20 */}
            <div className="bg-white rounded-xl p-6 sm:p-8 border border-slate-200">
              <h3 className="text-lg sm:text-xl font-bold text-slate-900 mb-4 flex items-center gap-2">
                <AlertCircle className="w-5 h-5 text-teal-600" /> Important Scope Information
              </h3>
              <p className="text-slate-600 mb-4">
                This service is a post-registration monitoring tool. Please note:
              </p>
              <ul className="space-y-3 text-sm text-slate-600">
                <li className="flex items-start gap-2"><span className="text-slate-400 font-bold mt-0.5">1.</span> This tool does not replace full similarity searches or pre-filing clearance</li>
                <li className="flex items-start gap-2"><span className="text-slate-400 font-bold mt-0.5">2.</span> It does not provide legal advice or legal conclusions</li>
                <li className="flex items-start gap-2"><span className="text-slate-400 font-bold mt-0.5">3.</span> It does not assess goodwill, reputation, or acquired distinctiveness</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-16 sm:py-24 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-black text-slate-900 mb-4">Volume-Based Pricing</h2>
            <p className="text-base sm:text-lg text-slate-600 max-w-3xl mx-auto mb-4">
              Our pricing is designed to encourage proactive brand protection across your entire UK trade mark portfolio, 
              rather than selective or partial monitoring.
            </p>
            <p className="text-sm text-slate-500 max-w-2xl mx-auto">
              Premium professional services with transparent volume discounts.
            </p>
          </div>
          <PricingCalculator handleSubscribe={handleSubscribe} isProcessing={isProcessing} />
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 mb-12">
            {PRICING_TIERS.map((tier, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.1 }}
                className={`rounded-xl p-6 border-2 ${tier.discount === 30 ? "border-teal-600 bg-teal-50" : "border-slate-200 bg-white"}`}>
                {tier.discount === 30 && <div className="text-xs font-bold text-teal-600 mb-2">BEST VALUE</div>}
                <h3 className="font-bold text-lg text-slate-900 mb-1">{tier.label}</h3>
                <div className="flex items-baseline gap-1 mb-2"><span className="text-3xl font-black text-teal-600">£{tier.monthlyPerTM}</span><span className="text-slate-500 text-sm">/TM/month</span></div>
                <p className="text-sm text-slate-500 mb-3">£{tier.yearlyPerTM}/TM/year</p>
                {tier.discount > 0 && <div className="inline-block bg-amber-100 text-amber-800 text-xs font-bold px-2 py-1 rounded">SAVE {tier.discount}%</div>}
              </motion.div>
            ))}
          </div>
          <div className="bg-slate-50 rounded-2xl p-6 sm:p-8 border border-slate-200 mb-12">
            <h3 className="text-xl font-bold text-slate-900 mb-4 flex items-center gap-2"><Check className="w-6 h-6 text-teal-600" />Portfolio-Wide Discounts</h3>
            <p className="text-slate-600 mb-4"><strong>Important:</strong> Where you submit multiple trade marks, the applicable discounted rate applies to <strong>ALL trade marks</strong> in your portfolio, not just incremental additions.</p>
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="bg-white rounded-lg p-4 border border-slate-200"><p className="text-sm font-medium text-slate-900 mb-2">Example: 8 Trade Marks</p><p className="text-sm text-slate-600">All 8 trade marks charged at <strong className="text-teal-600">£16/month each</strong> = £128/month</p><p className="text-xs text-slate-400 mt-1">vs. £160/month at standard rate (save £32/month)</p></div>
              <div className="bg-white rounded-lg p-4 border border-slate-200"><p className="text-sm font-medium text-slate-900 mb-2">Example: 12 Trade Marks</p><p className="text-sm text-slate-600">All 12 trade marks charged at <strong className="text-teal-600">£14/month each</strong> = £168/month</p><p className="text-xs text-slate-400 mt-1">vs. £240/month at standard rate (save £72/month)</p></div>
            </div>
          </div>
          <div className="bg-teal-600 rounded-2xl p-6 sm:p-8 text-center mb-12">
            <h3 className="text-xl font-bold text-white mb-2">Additional 10% Off Annual Prepayment</h3>
            <p className="text-teal-100 mb-4 max-w-2xl mx-auto">Clients who choose to pay annually in advance receive a further 10% discount on the total annual fee, calculated at the applicable volume-based rate.</p>
            <div className="grid sm:grid-cols-2 gap-4 max-w-2xl mx-auto">
              <div className="bg-white/20 rounded-lg px-4 py-3"><p className="text-white font-medium text-sm">Example: 8 TMs yearly = £1,382.40/year <span className="text-teal-200">(vs. £1,536 standard)</span></p></div>
              <div className="bg-white/20 rounded-lg px-4 py-3"><p className="text-white font-medium text-sm">Example: 3 TMs yearly = £583.20/year <span className="text-teal-200">(vs. £648 standard)</span></p></div>
            </div>
          </div>
          <div className="text-center mb-8"><h3 className="text-xl font-bold text-slate-900 mb-6">What's Included With Every Trade Mark</h3></div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { icon: ScanEye, title: "Pattern-Based Analysis", desc: "Visual, phonetic & dominant element similarity detection" },
              { icon: FileText, title: "Professional Reports", desc: "Expert-prepared reports highlighting applications of interest" },
              { icon: Clock, title: "Deadline Tracking", desc: "Never miss the 2-month opposition window" },
              { icon: Bell, title: "Specialist Consultation", desc: "Access to trade mark specialists included with your subscription" }
            ].map((feature, i) => (
              <div key={i} className="bg-slate-50 rounded-xl p-5 border border-slate-200">
                <feature.icon className="w-8 h-8 text-teal-600 mb-3" />
                <h4 className="font-bold text-slate-900 mb-1">{feature.title}</h4>
                <p className="text-sm text-slate-600">{feature.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-slate-900">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-heading font-black text-white mb-4">Trusted by Businesses Since 1984</h2>
            <p className="text-slate-400 mb-2">Join thousands of businesses who trust National Business Register for IP protection.</p>
            <a href="https://nationalbusinessregister.co.uk/" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-teal-400 hover:text-teal-300 text-sm font-medium transition-colors">
              Learn more about NBR <ExternalLink className="w-3.5 h-3.5" />
            </a>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              { quote: "We've been associated with NBR for many years and have always been happy with the quality of service provided especially in relation to protecting our trademarks.", author: "John Machin", role: "Director, The Universal Improvement Company" },
              { quote: "The advice and guidance provided is first class and we would not hesitate in recommending to any business seeking advice or guidance relating to Intellectual Property.", author: "Hyder Haniffa", role: "CEO, Unisnacks Europe Limited" },
              { quote: "We have placed our trust in NBR for nearly three decades and will continue to do so! Our trademark is our most valued asset.", author: "Energi International", role: "Co. Ltd" }
            ].map((testimonial, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.1 }} className="bg-slate-800 rounded-xl p-6 border border-slate-700">
                <div className="flex gap-1 mb-4">{[...Array(5)].map((_, j) => <Star key={j} className="w-4 h-4 text-amber-400 fill-amber-400" />)}</div>
                <p className="text-slate-300 mb-4 leading-relaxed">"{testimonial.quote}"</p>
                <div><p className="font-semibold text-white">{testimonial.author}</p><p className="text-sm text-slate-400">{testimonial.role}</p></div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Book a Demo - Point 1 (Teams clarity) */}
      <section id="demo" className="py-20 px-4 sm:px-6 lg:px-8 bg-slate-50">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-heading font-black text-slate-900 mb-4">Book a Demo</h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              See NBR Trade Mark Monitoring in action. Schedule a personalised Microsoft Teams demonstration 
              with our team and discover how we can help protect your trade marks.
            </p>
          </div>
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} className="bg-white rounded-2xl shadow-xl border border-slate-200 overflow-hidden">
            <div className="grid md:grid-cols-2">
              <div className="p-8 md:p-10 bg-gradient-to-br from-teal-600 to-teal-700 text-white">
                <div className="w-14 h-14 bg-white/20 rounded-xl flex items-center justify-center mb-6"><Video className="w-7 h-7" /></div>
                <h3 className="text-2xl font-bold mb-4">What to Expect</h3>
                <ul className="space-y-4">
                  {[
                    "Live walkthrough via Microsoft Teams",
                    "See how we monitor newly filed UK trade mark applications",
                    "Understand our reporting and review process",
                    "Get answers to your specific questions",
                    "Learn how we support opposition decisions"
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-3"><Check className="w-5 h-5 text-teal-200 flex-shrink-0 mt-0.5" /><span className="text-teal-50">{item}</span></li>
                  ))}
                </ul>
                <div className="mt-8 pt-6 border-t border-teal-500/30">
                  <p className="text-teal-100 text-sm"><Clock className="w-4 h-4 inline mr-2" />30-minute Microsoft Teams session with our IP experts</p>
                </div>
              </div>
              <div className="p-8 md:p-10 flex flex-col items-center justify-center">
                <Video className="w-16 h-16 text-teal-600 mb-6" />
                <h3 className="text-xl font-bold text-slate-900 mb-2 text-center">Schedule Your Teams Call</h3>
                <p className="text-slate-500 text-center mb-6">Choose a time that works for you</p>
                <Button size="lg" onClick={() => window.open(BOOKINGS_URL, '_blank')} className="bg-teal-600 hover:bg-teal-700 text-white px-8 py-6 shadow-lg shadow-teal-600/20" data-testid="book-demo-btn">
                  <Video className="w-5 h-5 mr-2" />Book Teams Demo
                </Button>
                <p className="text-xs text-slate-400 mt-4 text-center">Or call us directly: 0800 069 9090</p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Newsletter Signup - Point 11 */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white border-y border-slate-100">
        <div className="max-w-3xl mx-auto text-center">
          <Mail className="w-10 h-10 text-teal-600 mx-auto mb-4" />
          <h2 className="text-2xl sm:text-3xl font-heading font-black text-slate-900 mb-3">
            Stay Updated
          </h2>
          <p className="text-slate-600 mb-6">
            Subscribe to receive trade mark monitoring insights, case studies, and updates from our expert team.
          </p>
          <NewsletterSignup />
          <p className="text-xs text-slate-400 mt-4">We respect your privacy. Unsubscribe at any time.</p>
        </div>
      </section>

      {/* FAQ Quick Link */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-slate-50">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-2xl sm:text-3xl font-heading font-black text-slate-900 mb-4">Have Questions?</h2>
          <p className="text-slate-600 mb-6">Find answers about our monitoring service, pricing, registry coverage, and how we help protect your brand.</p>
          <Link to="/faq"><Button size="lg" className="bg-teal-600 hover:bg-teal-700 text-white px-8 py-5" data-testid="faq-link-btn">View Frequently Asked Questions <ArrowRight className="w-4 h-4 ml-2" /></Button></Link>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-teal-600">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-heading font-black text-white mb-4">Protect Your Entire Portfolio</h2>
          <p className="text-teal-100 text-lg mb-6 max-w-2xl mx-auto">
            The more trade marks you monitor, the more you save. Our volume-based pricing makes it 
            cost-effective to protect your entire UK trade mark portfolio.
          </p>
          <ul className="text-left max-w-md mx-auto mb-8 space-y-3">
            <li className="flex items-center gap-3 text-white/90 text-sm"><Check className="w-5 h-5 text-teal-200 flex-shrink-0" />From £14/TM/month with volume discounts</li>
            <li className="flex items-center gap-3 text-white/90 text-sm"><Check className="w-5 h-5 text-teal-200 flex-shrink-0" />Up to 30% off for larger portfolios</li>
            <li className="flex items-center gap-3 text-white/90 text-sm"><Check className="w-5 h-5 text-teal-200 flex-shrink-0" />Additional 10% off with annual prepayment</li>
          </ul>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button size="lg" onClick={handleSubscribe} className="bg-white text-teal-700 hover:bg-slate-100 px-8 py-6 text-base shadow-lg" data-testid="subscribe-btn-cta">
              <ShieldCheck className="w-5 h-5 mr-2" />Start Monitoring my Trade Mark
            </Button>
            <Button variant="outline" size="lg" onClick={() => window.open(BOOKINGS_URL, '_blank')} className="border-white text-white hover:bg-white/10 px-8 py-6 text-base" data-testid="request-sample-btn">
              <Video className="w-4 h-4 mr-2" />Book a Teams Demo
            </Button>
          </div>
          <p className="text-teal-200 text-sm mt-6">Or call us directly: <a href="tel:08000699090" className="underline font-medium">0800 069 9090</a></p>
        </div>
      </section>

      {/* Footer - Point 5 */}
      <footer className="bg-slate-900 py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-4 gap-8 mb-12">
            <div className="md:col-span-2">
              <div className="flex items-center gap-4 mb-4">
                <img src={NBR_LOGO} alt="National Business Register" className="h-20 w-auto brightness-0 invert" />
                <div>
                  <span className="font-heading font-black text-xl text-white">NBR</span>
                  <span className="text-xs text-teal-400 tracking-widest uppercase ml-1 block sm:inline">Trade Mark Monitoring</span>
                </div>
              </div>
              <p className="text-slate-400 text-sm max-w-md mb-4">
                NBR Trade Mark Monitoring is a product of National Business Register, the UK's leading 
                intellectual property experts since 1984.
              </p>
              <a href="https://nationalbusinessregister.co.uk/" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-teal-400 hover:text-teal-300 text-sm font-medium transition-colors">
                Visit nationalbusinessregister.co.uk <ExternalLink className="w-3.5 h-3.5" />
              </a>
            </div>
            <div>
              <h4 className="font-bold text-white mb-4">Contact</h4>
              <div className="space-y-3 text-sm text-slate-400">
                <a href="tel:08000699090" className="flex items-center gap-2 hover:text-teal-400 transition-colors"><Phone className="w-4 h-4" />0800 069 9090</a>
                <a href="mailto:info@nbrg.co.uk" className="flex items-center gap-2 hover:text-teal-400 transition-colors"><Mail className="w-4 h-4" />info@nbrg.co.uk</a>
                <div className="flex items-start gap-2"><MapPin className="w-4 h-4 mt-0.5 flex-shrink-0" /><span>Suite 7, The Courtyard, Russell House<br />6 Doctors Lane, Henley-in-Arden<br />Warwickshire, B95 5AW</span></div>
              </div>
            </div>
            <div>
              <h4 className="font-bold text-white mb-4">Quick Links</h4>
              <div className="space-y-2 text-sm">
                <a href="https://nationalbusinessregister.co.uk" target="_blank" rel="noopener noreferrer" className="block text-slate-400 hover:text-teal-400 transition-colors">About NBR</a>
                <a href="https://nationalbusinessregister.co.uk/registering-for-a-trade-mark/" target="_blank" rel="noopener noreferrer" className="block text-slate-400 hover:text-teal-400 transition-colors">Trade Mark Services</a>
                <Link to="/about" className="block text-slate-400 hover:text-teal-400 transition-colors">About You</Link>
                <Link to="/faq" className="block text-slate-400 hover:text-teal-400 transition-colors">FAQs</Link>
                <Link to="/blog" className="block text-slate-400 hover:text-teal-400 transition-colors">Blog</Link>
                <a href="https://nationalbusinessregister.co.uk/contact/" target="_blank" rel="noopener noreferrer" className="block text-slate-400 hover:text-teal-400 transition-colors">Contact Us</a>
                <a href="https://nationalbusinessregister.co.uk/wp-content/uploads/2025/01/NBR-Privacy-Policy-02-01-2025.pdf" target="_blank" rel="noopener noreferrer" className="block text-slate-400 hover:text-teal-400 transition-colors">Privacy Policy</a>
              </div>
            </div>
          </div>
          <div className="border-t border-slate-800 pt-8 text-center text-sm text-slate-400">
            <p>&copy; {new Date().getFullYear()} National Business Register Group Ltd. All rights reserved.</p>
            <p className="mt-1">Assisting with Trade Marks &amp; Name Protection Since 1984</p>
          </div>
        </div>
      </footer>

      {/* Back to Top - Mobile */}
      {showBackToTop && (
        <button onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })} className="fixed bottom-6 right-6 z-50 w-12 h-12 bg-teal-600 text-white rounded-full shadow-lg hover:bg-teal-700 transition-all flex items-center justify-center md:hidden" aria-label="Back to top" data-testid="back-to-top-btn">
          <ChevronUp className="w-6 h-6" />
        </button>
      )}
    </div>
  );
};

export default LandingPage;
